import type { ListPurchaseOrdersInput } from "@nexxt/contracts/procurement";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { procurementClient } from "../client";

// Server state of the purchase orders (pattern.md §10): one factory, the same keys for loaders, screens and
// mutations. Lines, proposals, revisions and the state of the sync of a PO keep their keys under `of`, so whatever
// reloads the POs reloads them too.

/** The filters of the list input (`listPurchaseOrdersInput`), without the paging the queries add. */
export type PurchaseOrderFilters = Pick<
  ListPurchaseOrdersInput,
  "status" | "brand" | "supplier" | "season" | "destination" | "bcSyncStatus" | "q"
> & {
  pendingChanges?: boolean | undefined;
  asSupplier?: boolean | undefined;
  /** Confirmed, with units no inbound sent announces yet (ADR-170): the documents the supplier has to upload. */
  toAnnounce?: boolean | undefined;
  /** Not confirmed after the last reminder and a cadence (B-37, ADR-245): Operations' queue. */
  remindersOver?: boolean | undefined;
  /** The sets of the app's queues "To do", as a JSON array (ADR-302). */
  anyOf?: string | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: ["purchase-orders"] as const,
  /** Prefix of every cached list: a write puts the PO it gets back in each of them. */
  lists: ["purchase-orders", "list"] as const,
  /** Prefix of every cached header: by id or by number, as the URL names it. */
  details: ["purchase-orders", "detail"] as const,
  /** Prefix of what is cached of one PO besides its header: lines, proposals, revisions, sync, history. */
  of: (purchaseOrderId: string) => ["purchase-orders", "of", purchaseOrderId] as const,
};

export const purchaseOrderQueries = {
  ...keys,

  list: (filters: PurchaseOrderFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        procurementClient().listPurchaseOrders({ ...filters, page: pageParam, pageSize: PAGE_SIZE }, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** By id or by number. */
  detail: (key: string) =>
    queryOptions({
      queryKey: [...keys.details, key],
      queryFn: ({ signal }) => procurementClient().getPurchaseOrder({ key }, signal),
    }),

  history: (purchaseOrderId: string) =>
    infiniteQueryOptions({
      queryKey: [...keys.of(purchaseOrderId), "history"],
      queryFn: ({ pageParam, signal }) =>
        procurementClient().getPurchaseOrderHistory(
          { purchaseOrderId, ...(pageParam && { cursor: pageParam }) },
          signal,
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  /** The values of the PO filters and form (ADR-048): they change rarely. */
  options: () =>
    queryOptions({
      queryKey: [...keys.all, "options"],
      queryFn: ({ signal }) => procurementClient().getPurchaseOrderOptions({}, signal),
      staleTime: 10 * 60_000,
    }),
};
